import type { Provenance, Revision, RevisionContent } from '../schema/v1'

/**
 * Operaciones sobre revisiones (§3.1). Las revisiones son inmutables: editar crea una revisión
 * hija. Lo único mutable es el puntero a la revisión principal del arreglo.
 */
export interface NewRevisionInput {
  /** ID global de la revisión nueva: lo genera quien llama, el dominio no usa aleatoriedad. */
  id: string
  /** Fecha ISO. El dominio no lee el reloj. */
  now: string
  author: Revision['author']
  reason?: string
}

export function createChildRevision(
  parent: Revision,
  content: RevisionContent,
  input: NewRevisionInput,
): Revision {
  const revision: Revision = {
    id: input.id,
    arrangementId: parent.arrangementId,
    parentRevisionId: parent.id,
    author: input.author,
    createdAt: input.now,
    content,
  }
  if (input.reason) revision.reason = input.reason
  return revision
}

/** Igualdad profunda de dos contenidos, sin depender del orden de las claves. */
export function sameContent(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  const left = a as Record<string, unknown>
  const right = b as Record<string, unknown>
  const keys = Object.keys(left).filter((key) => left[key] !== undefined)
  const others = Object.keys(right).filter((key) => right[key] !== undefined)
  if (keys.length !== others.length) return false
  return keys.every((key) => sameContent(left[key], right[key]))
}

/** Los datos de cabecera que se editan en el formulario. `null` quita el dato. */
export interface MetadataPatch {
  shapeKey?: string | null
  capo?: number
  tempoBpm?: number | null
  timeSignature?: string | null
  notes?: string | null
}

const USER: Provenance = { kind: 'user' }

/**
 * Aplica los cambios del formulario. Cada dato que cambia pasa a ser «tuyo» en la procedencia,
 * y el que no cambia conserva la suya. Si nada cambia, devuelve la misma ficha.
 */
export function applyMetadata(content: RevisionContent, patch: MetadataPatch): RevisionContent {
  const next: RevisionContent = { ...content, provenance: { ...content.provenance } }
  let changed = false

  if (patch.shapeKey !== undefined) {
    const value =
      patch.shapeKey === null || patch.shapeKey.trim() === '' ? undefined : patch.shapeKey.trim()
    if (value !== content.shapeKey) {
      changed = true
      if (value === undefined) {
        delete next.shapeKey
        delete next.provenance.shapeKey
      } else {
        next.shapeKey = value
        next.provenance.shapeKey = USER
      }
    }
  }

  if (patch.capo !== undefined && patch.capo !== content.capo) {
    changed = true
    next.capo = patch.capo
    next.provenance.capo = USER
  }

  if (patch.tempoBpm !== undefined && patch.tempoBpm !== (content.tempo.reference?.bpm ?? null)) {
    changed = true
    if (patch.tempoBpm === null) {
      next.tempo = {}
      delete next.provenance['tempo.reference']
    } else {
      next.tempo = { reference: { bpm: patch.tempoBpm } }
      next.provenance['tempo.reference'] = USER
    }
  }

  if (
    patch.timeSignature !== undefined &&
    (patch.timeSignature ?? undefined) !== content.timeSignature
  ) {
    changed = true
    if (patch.timeSignature === null) {
      delete next.timeSignature
      delete next.provenance.timeSignature
    } else {
      next.timeSignature = patch.timeSignature
      next.provenance.timeSignature = USER
    }
  }

  if (patch.notes !== undefined) {
    const value = patch.notes === null || patch.notes === '' ? undefined : patch.notes
    if (value !== content.notes) {
      changed = true
      if (value === undefined) delete next.notes
      else next.notes = value
    }
  }

  return changed ? next : content
}

/** ¿Queda algún dato sin aceptar por ti? */
export function needsReview(content: RevisionContent): boolean {
  return Object.values(content.provenance).some((entry) => entry.needsReview === true)
}

/** «Revisado» significa aceptado por ti, no exactitud musical garantizada (§3.3). */
export function acceptReview(content: RevisionContent): RevisionContent {
  if (!needsReview(content)) return content
  const provenance: Record<string, Provenance> = {}
  for (const [path, entry] of Object.entries(content.provenance)) {
    provenance[path] = entry.needsReview ? { ...entry, needsReview: false } : entry
  }
  return { ...content, provenance }
}
